/* eslint-disable no-template-curly-in-string */
import { Footer, FormElementInput, Screen, WhatsappFlow } from "../models";

function buildScreenHierarchy(screens: Screen[]): Map<string, string[]> {
  const hierarchy = new Map<string, string[]>();
  const visited = new Set<string>();

  // Find starting screen (no incoming references)
  const targetScreens = new Set<string>();
  screens.forEach((screen) => {
    const footer = screen.layout.children[0].children.find(
      (child) =>
        child.type === "Footer" && (child as any)["on-click-action"]?.next
    ) as any;

    if (footer?.["on-click-action"]?.next) {
      targetScreens.add(footer["on-click-action"].next.name);
    }
  });

  const startingScreen = screens.find((s) => !targetScreens.has(s.id));

  if (!startingScreen) {
    // Fallback: use first screen
    hierarchy.set(screens[0].id, [screens[0].id]);
    buildFromScreen(screens[0], [screens[0].id]);
  } else {
    buildFromScreen(startingScreen, [startingScreen.id]);
  }

  function buildFromScreen(screen: Screen, currentPath: string[]) {
    if (visited.has(screen.id)) return;
    visited.add(screen.id);

    hierarchy.set(screen.id, [...currentPath]);

    // Find next screen
    const footer = screen.layout.children[0].children.find(
      (child) =>
        child.type === "Footer" && (child as any)["on-click-action"]?.next
    ) as any;

    if (footer?.["on-click-action"]?.next) {
      const nextScreenId = footer["on-click-action"].next.name;
      const nextScreen = screens.find((s) => s.id === nextScreenId);
      if (nextScreen) {
        buildFromScreen(nextScreen, [...currentPath, nextScreenId]);
      }
    }
  }

  return hierarchy;
}

// Get all form fields from a screen (excluding Footer)
function getFormFields(screen: Screen): FormElementInput[] {
  return screen.layout.children[0].children.filter(
    (child) => (child as any).name && child.type !== "Footer"
  ) as FormElementInput[];
}

// Get all previous screens in the flow hierarchy
function getPreviousScreens(
  currentScreenId: string,
  hierarchy: Map<string, string[]>
): string[] {
  const path = hierarchy.get(currentScreenId) || [];
  const currentIndex = path.indexOf(currentScreenId);
  return path.slice(0, currentIndex);
}

// Create payload for navigation (current screen data + all previous screen data)
function createNavigationPayload(
  currentScreenId: string,
  currentScreenFields: FormElementInput[],
  previousScreens: string[],
  allScreens: Screen[]
): Record<string, string> {
  const payload: Record<string, string> = {};

  // Add current screen form data
  currentScreenFields.forEach((field) => {
    if (field.name) {
      payload[`${currentScreenId}_${field.name}`] = `\${form.${field.name}}`;
    }
  });

  // Add previous screens data using data reference
  previousScreens.forEach((screenId) => {
    const screen = allScreens.find((s) => s.id === screenId);
    if (screen) {
      const fields = getFormFields(screen);
      fields.forEach((field: any) => {
        if (field.name) {
          payload[
            `${screenId}_${field.name}`
          ] = `\${data.${screenId}_${field.name}}`;
        }
      });
    }
  });

  return payload;
}

// Create complete action payload (all screens data)
function createCompletePayload(
  allScreens: Screen[],
  hierarchy: Map<string, string[]>,
  completeScreenId: string
): Record<string, string> {
  const payload: Record<string, string> = {};

  const allPreviousScreens = getPreviousScreens(completeScreenId, hierarchy);

  // Add all previous screens data
  allPreviousScreens.forEach((screenId) => {
    const screen = allScreens.find((s) => s.id === screenId);
    if (!screen) return;
    const fields = getFormFields(screen);
    fields.forEach((field: any) => {
      if (field.name) {
        // Use clean field name for final payload
        payload[field.name] = `\${data.${screenId}_${field.name}}`;
      }
    });
  });

  // Add current screen data if it has form fields
  const currentScreen = allScreens.find((s) => s.id === completeScreenId);
  if (currentScreen) {
    const currentFields = getFormFields(currentScreen);
    currentFields.forEach((field: any) => {
      if (field.name) {
        payload[field.name] = `\${form.${field.name}}`;
      }
    });
  }

  return payload;
}

type ExtractedDataField = {
  type: "string";
  __example__: string;
};

type ExtractedData = Record<string, ExtractedDataField>;

export function extractDataFieldsFromFlow(screen: Screen): ExtractedData {
  const extractedData: ExtractedData = {};

  // Iterate through all screens and form elements
  screen.layout.children.forEach((form) => {
    form.children.forEach((element) => {
      if (element.type === "Footer") {
        const payload = element["on-click-action"].payload;

        // Check all payload values for ${data.X} patterns
        Object.values(payload).forEach((value) => {
          if (typeof value === "string") {
            const dataFieldRegex = /\$\{data\.([^}]+)\}/g;
            let match;

            while ((match = dataFieldRegex.exec(value)) !== null) {
              extractedData[match[1]] = {
                type: "string",
                __example__: "string",
              };
            }
          }
        });
      }
    });
  });

  return extractedData;
}

// Main function to validate and parse the flow
const validateAndParseTheFlow = (flow: WhatsappFlow): WhatsappFlow => {
  // Find the complete screen (terminal screen)
  const completeScreen = flow.flow.screens.find((screen) =>
    screen.layout.children[0].children.find(
      (child) =>
        child.type === "Footer" && child["on-click-action"]?.name === "complete"
    )
  );

  if (!completeScreen) {
    throw new Error("No complete screen found in the flow");
  }

  const completeScreenId = completeScreen.id;

  // Build screen hierarchy
  const hierarchy = buildScreenHierarchy(flow.flow.screens);

  const fieldsMap = flow.flow.screens
    .flatMap((f) => f.layout.children[0].children.filter((f: any) => f.name))
    .reduce((acc, field: any) => {
      acc[field.name] = field.label;
      return acc;
    }, {} as Record<string, any>);

  // Process each screen
  flow.flow.screens = flow.flow.screens.map((screen, index) => {
    // Set terminal flag
    screen.terminal = screen.id === completeScreenId;

    // Process children (form elements)
    screen.layout.children[0].children = screen.layout.children[0].children.map(
      (child) => {
        if ((child as any).customMeta) {
          delete (child as any).customMeta;
        }
        if (child.type === "Footer" && child["on-click-action"]) {
          const action = child["on-click-action"];

          if (action.name === "navigate" && action.next) {
            // Navigation footer - create payload with current + previous data
            const currentFields = getFormFields(screen);
            const previousScreens = getPreviousScreens(screen.id, hierarchy);

            action.payload = {
              ...createNavigationPayload(
                screen.id,
                currentFields,
                previousScreens,
                flow.flow.screens
              ),
              init_values: "${data.init_values}",
            };
          } else if (action.name === "complete") {
            // Complete footer - create payload with all screens data
            const payload = createCompletePayload(
              flow.flow.screens,
              hierarchy,
              completeScreenId
            );
            action.payload = {
              ...payload,
              fieldsMap,
              completed: true,
              flowName: flow.name,
              flowId: flow.id,
              flowMetaId: flow.metaId,
              saveFormInContact: flow.saveFormInContact,
              integration: flow.integration,
              integrationConfig: flow.integrationConfig,
              message: flow.completionMessage,
            } as any;
          }
        }
        return child;
      }
    );

    screen.layout.children[0]["init-values"] = "${data.init_values}";
    return screen;
  });

  flow.flow.screens = flow.flow.screens.map((screen, index) => {
    const data = extractDataFieldsFromFlow(screen);
    if (Object.keys(data).length > 0) {
      screen.data = data;
    }
    delete screen.data?.init_values;
    screen.data = {
      ...screen.data,
      init_values: {
        type: "object",
        properties: {
          "1": {
            type: "string",
          },
        },
        __example__: {
          "1": "1",
        },
      },
    };
    return screen;
  });

  return flow;
};

// Helper function to debug the flow structure
function debugFlowStructure(flow: WhatsappFlow): void {
  const hierarchy = buildScreenHierarchy(flow.flow.screens);

  console.log("=== Flow Structure Debug ===");
  hierarchy.forEach((path, screenId) => {
    console.log(`Screen ${screenId}: ${path.join(" → ")}`);
  });

  flow.flow.screens.forEach((screen) => {
    console.log(`\nScreen ${screen.id} (terminal: ${screen.terminal}):`);
    const fields = getFormFields(screen);
    fields.forEach((field) => {
      console.log(`  - Field: ${field.name} (${field.type})`);
    });

    const footer = screen.layout.children[0].children.find(
      (child) => child.type === "Footer"
    ) as Footer;
    if (footer?.["on-click-action"]) {
      const action = footer["on-click-action"] as any;
      console.log(`  - Footer Action: ${action.name}`);
      if (action.payload) {
        console.log(
          `  - Payload Keys: ${Object.keys(action.payload).join(", ")}`
        );
      }
    }
  });
}

function convertFlowDataToResponseFormat(
  flowData: WhatsappFlow,
  formSubmissionData: Record<string, any>
) {
  // Extract form fields from the flow data
  const screens = flowData.flow.screens;
  const formElements: any = [];
  const fieldsMap: any = {};

  // Find all form elements and build fieldsMap
  screens.forEach((screen) => {
    if (screen.layout && screen.layout.children) {
      screen.layout.children.forEach((child) => {
        if (child.type === "Form" && child.children) {
          child.children.forEach((element: any) => {
            if (element.name) {
              fieldsMap[element.name] = element.label;
              formElements.push({
                name: element.name,
                label: element.label,
                type: element.type,
                screenTitle: screen.title,
              });
            }
          });
        }
      });
    }
  });

  // Build the response text array
  const responseText: any = [];

  // Process each screen
  screens.forEach((screen, screenIndex) => {
    const screenTitle = screen.title || "Form";
    const elements = formElements.filter(
      (element: any) => element.screenTitle === screenTitle
    );

    if (elements.length === 0) {
      // Skip screens without form elements
      return;
    }

    // Add separator before new screen (except for the first screen)
    if (screenIndex > 0) {
      responseText.push("--------------------------------------------");
    }

    // Add screen title as header
    responseText.push(`## ${screenTitle}`);

    // Add form data for this screen
    elements.forEach((element: any) => {
      let fieldValue = formSubmissionData[element.name];
      if (Array.isArray(fieldValue)) {
        fieldValue = fieldValue.join(", ");
      }
      if (fieldValue === true) {
        fieldValue = "Checked";
      }
      if (fieldValue !== null && fieldValue !== undefined) {
        responseText.push(`**${element.label}**`);
        responseText.push(fieldValue);
      }
    });
  });

  // Create the final payload
  return {
    fields: {
      ...formSubmissionData,
      fieldsMap: fieldsMap,
    },
    responseText: responseText,
  };
}

export {
  validateAndParseTheFlow,
  debugFlowStructure,
  buildScreenHierarchy,
  createNavigationPayload,
  createCompletePayload,
  convertFlowDataToResponseFormat,
};
